import { PayloadAction } from "@reduxjs/toolkit";
import {
  IActionItem,
  ActionItemOrder,
  ArchiveApiResponse,
  ChangeTicketNameApiResponse,
  DetailsTabs,
  DuplicateApiResponse,
  IEffortActual,
  ITicketDescriptionForm,
  ITicketDetails,
  TicketActionItem,
  TicketActionItemUpdateTypeEnum,
  TicketAttachmentFile,
  TicketDetailsState,
} from "app/containers/Features/containers/Ticket/types";
import { AttachmentType } from "app/containers/Features/types";
import {
  addOrUpdateEffortActualEntry,
  removeEffortActualEntry,
  updateDescription,
} from "app/containers/Features/helper";
import { getAttachmentAssetType } from "app/containers/Features/containers/Ticket/components/AttachmentPreview/helper/getAttachmentAssetType";
import { EffortEstimatedPayload } from "app/containers/Features/types";
import { set } from "lodash";
import { createSlice } from "utils/@reduxjs/toolkit";
import { CustomFieldItem } from "../../TeamSettings/container/Projects/container/CustomFields/types";
import { initialState } from "./initialState";

type ActionItemsState = TicketDetailsState["actionItems"];

export function filterValidActionItemsFromApi(
  apiItems: IActionItem[] | null | undefined,
): IActionItem[] {
  if (!Array.isArray(apiItems)) return [];
  return apiItems.filter(
    (item): item is IActionItem =>
      item != null &&
      typeof item === "object" &&
      typeof item._id === "string" &&
      item._id.length > 0,
  );
}

const createActionItem = (label = "New checklist item"): TicketActionItem => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  isResolved: false,
  label,
});

const resetActionItemsEditorState = (actionItems: ActionItemsState) => {
  actionItems.newItemValue = "";
  actionItems.editingItemId = null;
  actionItems.editingValue = "";
  actionItems.newInlineItemId = null;
};

const getAttachmentById = (
  files: TicketAttachmentFile[],
  fileId: string | null,
) => {
  if (!fileId) return null;
  return files.find((file) => file.id === fileId) || null;
};

const mapTicketDetailsAttachmentToFile = (
  attachment: Partial<AttachmentType> & { _id?: string },
): TicketAttachmentFile | null => {
  const fileId = attachment.fileId || attachment._id;
  if (!fileId) return null;

  return {
    id: fileId,
    name: attachment.fileName || "Attachment",
    type: getAttachmentAssetType(attachment.mimeType, attachment.fileName),
    createdAt: attachment.createdAt ?? attachment.updatedAt,
    url: attachment.url,
    filePath: attachment.filePath,
  };
};

const setActionItemsFromTicketDetails = (
  actionItems: ActionItemsState,
  apiItems: IActionItem[],
) => {
  const list = filterValidActionItemsFromApi(apiItems);
  actionItems.items = list.map((item) => ({
    id: item._id,
    isResolved: !!item.completed,
    label: item.title || "",
  }));
  resetActionItemsEditorState(actionItems);
};

const setAllActionItemsResolved = (
  actionItems: ActionItemsState,
  isResolved: boolean,
) => {
  actionItems.items = actionItems.items.map((item) => ({
    ...item,
    isResolved,
  }));
};

const getInsertIndexByOrder = (
  ids: string[],
  order?: ActionItemOrder,
): number => {
  if (!order) return ids.length;

  const beforeIdx = order.before ? ids.indexOf(order.before) : -1;
  if (beforeIdx !== -1) return beforeIdx;

  const afterIdx = order.after ? ids.indexOf(order.after) : -1;
  if (afterIdx !== -1) return afterIdx + 1;

  return ids.length;
};

const moveItemByIndex = <T>(list: T[], from: number, to: number): T[] => {
  if (from < 0 || from >= list.length) return list;
  if (to < 0 || to > list.length) return list;
  if (from === to) return list;

  const next = [...list];
  const [movedItem] = next.splice(from, 1);
  if (!movedItem) return list;

  const destinationIndex = Math.min(to, next.length);
  next.splice(destinationIndex, 0, movedItem);
  return next;
};

export const ticketDetailsSlice = createSlice({
  name: "ticketDetailsState",
  initialState: initialState,
  reducers: {
    resetTicketDetailsSlice: () => {
      return initialState;
    },

    resetLocalActionItems(state) {
      state.actionItems.items = [];
      resetActionItemsEditorState(state.actionItems);
    },

    setActionItemsNewItemValue(state, action: PayloadAction<string>) {
      state.actionItems.newItemValue = action.payload;
    },

    setActionItemsEditingValue(state, action: PayloadAction<string>) {
      state.actionItems.editingValue = action.payload;
    },

    toggleActionItemResolved(state, action: PayloadAction<number>) {
      state.actionItems.items = state.actionItems.items.map((item, index) =>
        index === action.payload
          ? { ...item, isResolved: !item.isResolved }
          : item,
      );
    },

    checkAllActionItems(state) {
      setAllActionItemsResolved(state.actionItems, true);
    },

    uncheckAllActionItems(state) {
      setAllActionItemsResolved(state.actionItems, false);
    },

    deleteAllActionItems(state) {
      state.actionItems.items = [];
      state.details.result.actionItems = [];
      resetActionItemsEditorState(state.actionItems);
    },

    deleteActionItem(state, action: PayloadAction<number>) {
      state.actionItems.items = state.actionItems.items.filter(
        (_, index) => index !== action.payload,
      );
    },

    startActionItemRename(state, action: PayloadAction<number>) {
      const item = state.actionItems.items[action.payload];
      if (!item) return;
      state.actionItems.newInlineItemId = null;
      state.actionItems.editingItemId = item.id;
      state.actionItems.editingValue = item.label;
    },

    saveActionItemRename(state) {
      const { editingItemId, editingValue, newInlineItemId } =
        state.actionItems;
      if (!editingItemId) return;
      const updatedValue = editingValue.trim();

      if (!updatedValue) {
        if (editingItemId === newInlineItemId) {
          state.actionItems.items = state.actionItems.items.filter(
            (item) => item.id !== editingItemId,
          );
        }
        resetActionItemsEditorState(state.actionItems);
        return;
      }

      state.actionItems.items = state.actionItems.items.map((item) =>
        item.id === editingItemId ? { ...item, label: updatedValue } : item,
      );
      resetActionItemsEditorState(state.actionItems);
    },

    cancelActionItemRename(state) {
      const { editingItemId, newInlineItemId } = state.actionItems;
      if (editingItemId && editingItemId === newInlineItemId) {
        state.actionItems.items = state.actionItems.items.filter(
          (item) => item.id !== editingItemId,
        );
      }
      resetActionItemsEditorState(state.actionItems);
    },

    addActionItem(state, action: PayloadAction<string | undefined>) {
      const finalLabel = (
        action.payload ?? state.actionItems.newItemValue
      ).trim();
      if (!finalLabel) return;
      state.actionItems.items.push(createActionItem(finalLabel));
      state.actionItems.newItemValue = "";
    },

    requestCheckAllActionItems(
      _state,
      _action: PayloadAction<{
        projectId?: string;
        callback?: () => void;
      }>,
    ) {},

    requestUncheckAllActionItems(
      _state,
      _action: PayloadAction<{
        projectId?: string;
        callback?: () => void;
      }>,
    ) {},

    requestDeleteAllActionItems(
      _state,
      _action: PayloadAction<{
        projectId?: string;
        callback?: () => void;
      }>,
    ) {},

    requestToggleActionItemResolved(
      _state,
      _action: PayloadAction<{
        id: string;
        title: string;
        completed: boolean;
        projectId?: string;
        callback?: () => void;
      }>,
    ) {},

    requestCreateActionItem(
      _state,
      _action: PayloadAction<{
        title: string;
        order?: ActionItemOrder;
        projectId?: string;
        callback?: () => void;
      }>,
    ) {},

    requestUpdateActionItem(
      _state,
      _action: PayloadAction<{
        id: string;
        title: string;
        updateType: TicketActionItemUpdateTypeEnum;
        order?: ActionItemOrder;
        allowEmptyTitle?: boolean;
        projectId?: string;
        callback?: () => void;
      }>,
    ) {},

    requestDeleteActionItem(
      _state,
      _action: PayloadAction<{
        id: string;
        projectId?: string;
        callback?: () => void;
      }>,
    ) {},

    appendCreatedActionItem(state, action: PayloadAction<{ title: string }>) {
      const finalLabel = action.payload.title.trim();
      if (!finalLabel) return;
      state.actionItems.items.push(createActionItem(finalLabel));
      state.actionItems.newItemValue = "";
    },

    appendCreatedActionItemFromApi(
      state,
      action: PayloadAction<{ item: IActionItem; order?: ActionItemOrder }>,
    ) {
      const { item: apiItem, order } = action.payload;
      if (!apiItem?._id) return;

      const insertIndex = getInsertIndexByOrder(
        state.actionItems.items.map((item) => item.id),
        order,
      );
      state.actionItems.items.splice(insertIndex, 0, {
        id: apiItem._id,
        isResolved: !!apiItem.completed,
        label: apiItem.title || "",
      });
      state.actionItems.newItemValue = "";
      const detailsActionItems = [
        ...filterValidActionItemsFromApi(
          state.details.result.actionItems || [],
        ),
      ];
      const detailsInsertIndex = getInsertIndexByOrder(
        detailsActionItems.map((item) => item._id),
        order,
      );
      detailsActionItems.splice(detailsInsertIndex, 0, apiItem);
      state.details.result.actionItems = detailsActionItems;
    },

    updateActionItemTitleLocally(
      state,
      action: PayloadAction<{ id: string; title: string }>,
    ) {
      const { id, title } = action.payload;
      const updatedTitle = title.trim();
      if (!updatedTitle) return;
      state.actionItems.items = state.actionItems.items.map((item) =>
        item.id === id ? { ...item, label: updatedTitle } : item,
      );
    },

    setActionItemResolvedLocally(
      state,
      action: PayloadAction<{ id: string; isResolved: boolean }>,
    ) {
      const { id, isResolved } = action.payload;
      state.actionItems.items = state.actionItems.items.map((item) =>
        item.id === id ? { ...item, isResolved } : item,
      );
    },

    removeActionItemLocally(state, action: PayloadAction<{ id: string }>) {
      state.actionItems.items = state.actionItems.items.filter(
        (item) => item.id !== action.payload.id,
      );
    },

    reorderActionItemsLocally(
      state,
      action: PayloadAction<{ from: number; to: number }>,
    ) {
      const { from, to } = action.payload;
      state.actionItems.items = moveItemByIndex(
        state.actionItems.items,
        from,
        to,
      );
      state.details.result.actionItems = moveItemByIndex(
        state.details.result.actionItems || [],
        from,
        to,
      );
    },

    clearActionItemEditingState(state) {
      resetActionItemsEditorState(state.actionItems);
    },

    addActionItemAfter(state, action: PayloadAction<number>) {
      const newItem = createActionItem("");
      state.actionItems.items.splice(action.payload + 1, 0, newItem);
      state.actionItems.newInlineItemId = newItem.id;
      state.actionItems.editingItemId = newItem.id;
      state.actionItems.editingValue = "";
    },

    setActiveDetailsTab: (state, action: { payload: DetailsTabs }) => {
      state.activeDetailsTab = action?.payload;
    },

    fetchTicketDetails(
      state,
      action: PayloadAction<{
        ticketId: string;
        skipLoading?: boolean;
      }>,
    ) {
      if (!action.payload?.skipLoading) {
        state.details.loading = true;
      }
    },

    endGetDetailsProcess(state) {
      state.details.loading = false;
    },

    partiallyUpdateTicketDetails(state, action: PayloadAction<ITicketDetails>) {
      const payload = action.payload;
      const actionItems = filterValidActionItemsFromApi(payload?.actionItems);
      const nextAttachmentFiles = Array.isArray(payload?.attachments)
        ? payload.attachments
            .map((attachment) =>
              mapTicketDetailsAttachmentToFile(
                attachment as Partial<AttachmentType> & { _id?: string },
              ),
            )
            .filter((item): item is TicketAttachmentFile => item !== null)
        : [];

      const result: ITicketDetails = {
        ...payload,
        actionItems,
      };
      if (result.description) {
        result.description = updateDescription(result.description);
      }

      state.details = {
        ...state?.details,
        result,
      };
      setActionItemsFromTicketDetails(state.actionItems, actionItems);
      state.attachmentState.files = nextAttachmentFiles;

      const hasRenamingFile = nextAttachmentFiles.some(
        (file) => file.id === state.attachmentState.renamingFileId,
      );
      if (!hasRenamingFile) {
        state.attachmentState.renamingFileId = null;
        state.attachmentState.renameValue = "";
      }
    },

    toggleIsFavoriteValue(state) {
      if (state?.details?.result) {
        const cur = state.details?.result.isFavorite;
        state.details.result.isFavorite = cur === 1 ? 0 : 1;
      }
    },

    setTicketFavoriteFlag(state, action: PayloadAction<0 | 1>) {
      if (state?.details?.result) {
        state.details.result.isFavorite = action.payload;
      }
    },

    startDuplicateActionProcess(
      state,
      _action: PayloadAction<{
        callback?: (response: DuplicateApiResponse) => void;
      }>,
    ) {
      state.duplicateAction.loading = true;
    },

    endDuplicateActionProcess(state) {
      state.duplicateAction.loading = false;
    },

    startArchiveActionProcess(
      state,
      _action: PayloadAction<{
        callback?: (response: ArchiveApiResponse) => void;
      }>,
    ) {
      state.archiveAction.loading = true;
    },

    endArchiveActionProcess(state) {
      state.archiveAction.loading = false;
    },

    startChangeTicketNameActionProcess(
      state,
      _action: PayloadAction<{
        ticketName: string;
        callback?: (response: ChangeTicketNameApiResponse) => void;
      }>,
    ) {
      state.changeTicketNameAction.loading = true;
    },

    endChangeTicketNameActionProcess(state) {
      state.changeTicketNameAction.loading = false;
    },

    updateTicketName(state, action: PayloadAction<{ ticketName: string }>) {
      if (state.details.result) {
        state.details.result.ticketName = action?.payload?.ticketName;
      }
    },

    setTicketDetailsFormValue: (
      state,
      action: PayloadAction<{
        name: string;
        value: string | number | boolean | Date | string[];
      }>,
    ) => {
      set(state, `details.result.${action.payload.name}`, action.payload.value);
    },
    setTicketTagsFormValue: (
      state,
      action: PayloadAction<{
        ids: string | string[];
      }>,
    ) => {
      const { ids } = action.payload;
      const tagIds = state.details.result.tags ?? [];

      const incomingIds = Array.isArray(ids) ? ids : [ids];

      const allAlreadySelected = incomingIds.every((id) => tagIds.includes(id));

      if (allAlreadySelected) {
        state.details.result.tags = tagIds.filter(
          (id) => !incomingIds.includes(id),
        );
      } else {
        const toAdd = incomingIds.filter((id) => !tagIds.includes(id));
        state.details.result.tags = [...tagIds, ...toAdd];
      }
    },
    updateEffort(
      state,
      action: PayloadAction<{ effortEstimated: IEffortActual }>,
    ) {
      if (state.details.result) {
        const incoming = action?.payload?.effortEstimated;
        const exists = state.details.result.effortEstimated.some(
          (e) => e.memberId === incoming.memberId,
        );

        if (exists) {
          state.details.result.effortEstimated =
            state.details.result.effortEstimated.map((e) =>
              e.memberId === incoming.memberId ? incoming : e,
            );
        } else {
          state.details.result.effortEstimated = [
            ...state.details.result.effortEstimated,
            incoming,
          ];
        }

        const totalMinutes = state.details.result.effortEstimated.reduce(
          (sum, e) =>
            sum + (e.effort?.hours ?? 0) * 60 + (e.effort?.minutes ?? 0),
          0,
        );

        state.details.result.totalEffortEstimated = {
          hours: Math.floor(totalMinutes / 60),
          minutes: totalMinutes % 60,
        };
      }
    },

    toggleEffortEstimateMenu(state, action: PayloadAction<boolean>) {
      state.toggleEffortEstimateMenu = action?.payload;
    },

    requestUpdateEffortEstimated(
      state,
      action: PayloadAction<{
        effortEstimated: EffortEstimatedPayload;
        callback?: () => void;
      }>,
    ) {
      state.updateEffortEstimatedLoading = true;
    },

    setUpdateEffortEstimatedLoading(state, action: PayloadAction<boolean>) {
      state.updateEffortEstimatedLoading = action?.payload;
    },

    requestUpdateCustomFieldValue(
      _state,
      _action: PayloadAction<{
        fieldId: string;
        value: string | number | boolean | Date | string[];
        projectId?: string;
      }>,
    ) {},

    updateCustomFieldValueLocally(
      state,
      action: PayloadAction<{
        fieldId: string;
        value: string | number | boolean | Date | string[];
      }>,
    ) {
      const detailsData = state.details.result;
      if (!detailsData?.customFields?.length) return;

      const { fieldId, value } = action.payload;
      const customField = detailsData.customFields.find(
        (field: CustomFieldItem) => {
          const source = field as CustomFieldItem & {
            fieldId?: string;
            ticketFieldId?: string;
            customFieldId?: string;
          };
          return [
            source._id,
            source.fieldId,
            source.ticketFieldId,
            source.customFieldId,
          ]
            .filter(Boolean)
            .includes(fieldId);
        },
      );

      if (customField?.extraParameters) {
        customField.extraParameters.fieldValue = value as string | string[];
      }
    },

    // Effort Actual related reducers --
    deleteEffortActualEntryLocally: (
      state,
      action: PayloadAction<{
        effortId: string;
      }>,
    ) => {
      const { effortId } = action.payload;

      state.details.result = removeEffortActualEntry(
        [state.details.result],
        state.details.result._id,
        effortId,
      )[0];
    },

    // Ticket Description related reducers --
    setEnableTicketDescription(state, action: PayloadAction<boolean>) {
      state.enableTicketDescription = action.payload;
    },

    setEnableAddNewTemplateForm(state, action: PayloadAction<boolean>) {
      state.enableAddNewTemplateForm = action.payload;
    },

    setTicketDescriptionForm(
      state,
      action: PayloadAction<ITicketDescriptionForm>,
    ) {
      state.ticketDescriptionForm = action.payload;
    },

    setUpdateTicketDescriptionLoading(state, action: PayloadAction<boolean>) {
      state.updateTicketDescriptionLoading = action.payload;
    },

    requestUpdateTicketDescription(
      state,
      action: PayloadAction<{
        callback?: () => void;
      }>,
    ) {
      state.updateTicketDescriptionLoading = true;
    },

    requestUpdateTicketAttachments(
      _state,
      _action: PayloadAction<{
        attachments: string[];
        projectId: string;
        action: "add" | "remove";
        callback?: () => void;
      }>,
    ) {},

    setTicketDescriptionLocally(
      state,
      action: PayloadAction<ITicketDescriptionForm>,
    ) {
      const { description } = action.payload;
      if (!state.details.result) return;

      state.details.result.description = updateDescription({
        ...description,
        attachments: state.details.result.description.attachments || [],
        _id: state.details.result.description._id,
        attachmentsDetails:
          state.details.result.description.attachmentsDetails || [],
      });
    },
    // --

    addEffortActualEntryLocally: (
      state,
      action: PayloadAction<{
        ticketId: string;
        entry: IEffortActual;
      }>,
    ) => {
      const { ticketId, entry } = action.payload;
      state.details.result = addOrUpdateEffortActualEntry(
        [state.details.result],
        ticketId,
        entry,
      )[0];
    },

    updateEffortActualEntryLocally: (
      state,
      action: PayloadAction<{
        ticketId: string;
        entryId: string;
        updates: Partial<IEffortActual>;
      }>,
    ) => {
      const { ticketId, entryId, updates } = action.payload;
      state.details.result = addOrUpdateEffortActualEntry(
        [state.details.result],
        ticketId,
        updates,
        entryId,
      )[0];
    },

    // Attachment related reducers --

    prependAttachmentFiles(
      state,
      action: PayloadAction<{ files: TicketAttachmentFile[] }>,
    ) {
      const incomingFiles = action.payload.files || [];
      if (!incomingFiles.length) return;
      state.attachmentState.files = [
        ...incomingFiles,
        ...state.attachmentState.files,
      ];
    },

    startAttachmentRename(state, action: PayloadAction<{ fileId: string }>) {
      const file = getAttachmentById(
        state.attachmentState.files,
        action.payload.fileId,
      );
      if (!file) return;

      state.attachmentState.renamingFileId = file.id;
      state.attachmentState.renameValue = file.name;
    },

    setAttachmentRenameValue(state, action: PayloadAction<string>) {
      state.attachmentState.renameValue = action.payload;
    },

    requestRenameAttachment(
      state,
      _action: PayloadAction<{
        assetId: string;
        fileName: string;
        onRenamed?: () => void;
      }>,
    ) {
      state.attachmentState.renameAttachmentLoading = true;
    },

    renameAttachmentSucceeded(
      state,
      action: PayloadAction<{ assetId: string; fileName: string }>,
    ) {
      const { assetId, fileName } = action.payload;
      state.attachmentState.files = state.attachmentState.files.map((item) =>
        item.id === assetId ? { ...item, name: fileName } : item,
      );
      state.attachmentState.renameValue = "";
      state.attachmentState.renamingFileId = null;
      state.attachmentState.renameAttachmentLoading = false;
    },

    renameAttachmentFailed(state) {
      state.attachmentState.renameAttachmentLoading = false;
    },

    cancelAttachmentRename(state) {
      state.attachmentState.renameValue = "";
      state.attachmentState.renamingFileId = null;
      state.attachmentState.renameAttachmentLoading = false;
    },

    requestAttachmentDelete(state, action: PayloadAction<{ fileId: string }>) {
      const file = getAttachmentById(
        state.attachmentState.files,
        action.payload.fileId,
      );
      if (!file) return;

      state.attachmentState.fileToDeleteId = file.id;
      state.attachmentState.showDeleteModal = true;
    },

    openAttachmentPreview(
      state,
      action: PayloadAction<{ fileId: string; previewFileIds?: string[] }>,
    ) {
      const file = getAttachmentById(
        state.attachmentState.files,
        action.payload.fileId,
      );
      if (!file) return;

      state.attachmentState.previewFileId = file.id;
      state.attachmentState.previewFileIds =
        action.payload.previewFileIds || null;
      state.attachmentState.isPreviewOpen = true;
      state.attachmentState.previewUrlLoading = false;
    },

    requestAttachmentPreviewUrl(
      _state,
      _action: PayloadAction<{
        fileId: string;
        fileName: string;
        filePath?: string;
      }>,
    ) {},

    setAttachmentPreviewUrlLoading(state, action: PayloadAction<boolean>) {
      state.attachmentState.previewUrlLoading = action.payload;
    },

    updateAttachmentFileUrl(
      state,
      action: PayloadAction<{ fileId: string; url: string }>,
    ) {
      const { fileId, url } = action.payload;
      state.attachmentState.files = state.attachmentState.files.map((file) =>
        file.id === fileId ? { ...file, url } : file,
      );
    },

    setAttachmentPreviewFile(state, action: PayloadAction<{ fileId: string }>) {
      const file = getAttachmentById(
        state.attachmentState.files,
        action.payload.fileId,
      );
      if (!file) return;

      state.attachmentState.previewFileId = file.id;
    },

    requestAttachmentTextPreview(
      _state,
      _action: PayloadAction<{ fileUrl: string }>,
    ) {},

    setAttachmentTextPreviewLoading(
      state,
      action: PayloadAction<{ fileUrl: string }>,
    ) {
      state.attachmentState.textPreview = {
        fileUrl: action.payload.fileUrl,
        content: null,
        isLoading: true,
        hasError: false,
      };
    },

    setAttachmentTextPreviewSuccess(
      state,
      action: PayloadAction<{ fileUrl: string; content: string }>,
    ) {
      state.attachmentState.textPreview = {
        fileUrl: action.payload.fileUrl,
        content: action.payload.content,
        isLoading: false,
        hasError: false,
      };
    },

    setAttachmentTextPreviewError(
      state,
      action: PayloadAction<{ fileUrl: string }>,
    ) {
      state.attachmentState.textPreview = {
        fileUrl: action.payload.fileUrl,
        content: null,
        isLoading: false,
        hasError: true,
      };
    },

    resetAttachmentTextPreview(state) {
      state.attachmentState.textPreview = {
        fileUrl: null,
        content: null,
        isLoading: false,
        hasError: false,
      };
    },

    closeAttachmentPreview(state) {
      state.attachmentState.isPreviewOpen = false;
      state.attachmentState.previewFileId = null;
      state.attachmentState.previewFileIds = null;
      state.attachmentState.textPreview = {
        fileUrl: null,
        content: null,
        isLoading: false,
        hasError: false,
      };
    },

    cancelAttachmentDelete(state) {
      state.attachmentState.showDeleteModal = false;
      state.attachmentState.fileToDeleteId = null;
    },

    confirmAttachmentDelete(state) {
      const {
        fileToDeleteId,
        renamingFileId,
        previewFileId,
        files,
        previewFileIds,
      } = state.attachmentState;
      if (!fileToDeleteId) return;

      state.attachmentState.files = state.attachmentState.files.filter(
        (file) => file.id !== fileToDeleteId,
      );

      if (renamingFileId === fileToDeleteId) {
        state.attachmentState.renameValue = "";
        state.attachmentState.renamingFileId = null;
      }

      if (previewFileId === fileToDeleteId) {
        const scopedIds = previewFileIds || files.map((file) => file.id);
        const remainingScopedIds = scopedIds.filter(
          (id) => id !== fileToDeleteId,
        );
        const deletedFileIndex = scopedIds.findIndex(
          (id) => id === fileToDeleteId,
        );
        const fallbackFileId =
          remainingScopedIds[deletedFileIndex] ||
          remainingScopedIds[deletedFileIndex - 1] ||
          null;
        const fallbackFile =
          files.find((file) => file.id === fallbackFileId) || null;

        state.attachmentState.previewFileId = fallbackFile?.id || null;
        state.attachmentState.previewFileIds = previewFileIds
          ? remainingScopedIds
          : null;
        state.attachmentState.isPreviewOpen = !!fallbackFile;
      }

      state.attachmentState.showDeleteModal = false;
      state.attachmentState.fileToDeleteId = null;
    },
  },
});

export const ticketDetailsSliceName = ticketDetailsSlice?.name;

export const ticketDetailsSliceReducers = ticketDetailsSlice?.reducer;

export const ticketDetailsActions = ticketDetailsSlice?.actions;
